import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { bindActionCreators } from 'redux';
import { createSelector } from 'reselect';
import { Model } from 'survey-core';
import ChangeInsightsSettingForm from '../../components/change-insights/ChangeInsightsSettingsForm.jsx';
import ChangeInsightsCustomizeDashboardHeader from '../../components/headers/PageHeaders/Project/ChangeInsightsCustomizeDashboardHeader.jsx';
import { navigateToChangeInsightDetails } from '../../components/links/index.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import { mapFinalFormErrors } from '../../lib/utils.js';
import { selectCurrentOrganizationId } from '../../modules/auth.js';
import {
  retrieveChangeInsight,
  retrieveChangeInsightDetails,
  selectTemplates,
  updateChangeInsightDetails,
} from '../../modules/changeInsights.js';
import {
  retrieveInitiative,
  selectInitiative,
} from '../../modules/initiatives.js';
import { retrieveOrganization } from '../../modules/organizations.js';
import { selectProject } from '../../modules/projects.js';
import { formatedTemplate, QUESTION_TYPES_DASHBOARD } from './utils/index.jsx';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state, { projectId, initiativeId, initiativeType }) =>
  createSelector(
    selectInitiative(initiativeId, initiativeType),
    (initiative, organization) => ({
      initiative,
      organization,
      changeInsights: selectTemplates(state),
      organizationId: selectCurrentOrganizationId(state),
      project: selectProject(projectId),
    })
  )(state);

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      retrieveInitiative,
      retrieveChangeInsight,
      updateChangeInsightDetails,
      retrieveOrganization,
      retrieveChangeInsightDetails,
    },
    dispatch
  );

const ChangeInsightsSettingFormContainer = (props) => {
  const {
    slug,
    initiative,
    initiativeType,
    initiativeId,
    retrieveInitiative,
    updateChangeInsightDetails,
    retrieveChangeInsightDetails,
    retrieveChangeInsight,
  } = props;

  const [survey, setSurvey] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const randomId = () => Math.random().toString(36).slice(2, 7);

  const onSubmit = () => {
    setLoading(true);
    if (initiative?.surveyId) {
      updateChangeInsightDetails(initiative.surveyId, {
        dashboardSettings: JSON.stringify(data),
      })
        .then(() => {
          retrieveChangeInsightDetails(initiative.surveyId).then(() => {
            navigateToChangeInsightDetails();
          });
        })
        .catch(mapErrors)
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result.map((item, index) => ({ ...item, priority: index }));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = reorder(data, result.source.index, result.destination.index);
    setData(items);
  };

  const onDashboardShowChange = (itemId) => {
    setData(
      data.map((item, index) =>
        itemId === item.id
          ? { ...item, dashboardShow: !item.dashboardShow }
          : item
      )
    );
  };

  useEffect(() => {
    if (survey) {
      if (survey.dashboardSettings) {
        const dashboardSettings =
          typeof survey.dashboardSettings === 'string'
            ? JSON.parse(survey.dashboardSettings)
            : survey.dashboardSettings;
        // Ensure all items have draggableId
        const settingsWithIds = dashboardSettings.map((item) => ({
          ...item,
          draggableId: item.draggableId || randomId(),
        }));
        setData(settingsWithIds);
      } else {
        const parsedSurveyJson = formatedTemplate(survey);
        if (parsedSurveyJson) {
          const surveyModel = new Model(parsedSurveyJson);

          const defaultQuestions = surveyModel
            .getAllQuestions()
            .filter((question) =>
              QUESTION_TYPES_DASHBOARD.includes(question.jsonObj.type)
            )
            .map((question, index) => ({
              id: question.jsonObj.title,
              name: question.jsonObj.name,
              title: question.jsonObj.title
                ? question.jsonObj.title
                : question.jsonObj.name,
              priority: index,
              dashboardShow: true,
              draggableId: randomId(),
            }));

          setData(defaultQuestions);
        }
      }
    }
  }, [survey]);

  useEffect(() => {
    retrieveInitiative(initiativeType, initiativeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiativeType, initiativeId]);

  useEffect(() => {
    if (initiative?.surveyId) {
      retrieveChangeInsight(initiative.surveyId).then((res) => {
        setSurvey(res);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiative?.surveyId]);

  return (
    <React.Fragment>
      {initiative ? (
        <ChangeInsightsCustomizeDashboardHeader
          page="Customize Feedback Dashboard"
          project={initiative.project}
          slug={slug}
        />
      ) : null}
      <SubHeader flexJustify>
        <h2 className="m-r-10 m-l-16">Customize Feedback Dashboard</h2>
      </SubHeader>

      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  {data ? (
                    <Form
                      component={ChangeInsightsSettingForm}
                      onSubmit={onSubmit}
                      handleSubmit={onSubmit}
                      onDragEnd={onDragEnd}
                      onDashboardShowChange={onDashboardShowChange}
                      data={data}
                      loading={loading}
                    />
                  ) : null}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangeInsightsSettingFormContainer);
